import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';
import type {
  AdminPlanCount,
  AdminSubscriptionBreakdownResponse,
  AdminSubscriptionPlan,
} from '@/lib/types';

const PLAN_COLORS: Record<AdminSubscriptionPlan, string> = {
  monthly: '#018A9D',
  annual: '#0f766e',
  other: '#f59e0b',
  unknown: '#9ca3af',
};

const PLAN_LABELS: Record<AdminSubscriptionPlan, string> = {
  monthly: 'Monthly',
  annual: 'Annual',
  other: 'Other',
  unknown: 'Unknown',
};

const STORE_LABELS: Record<string, string> = {
  app_store: 'App Store',
  mac_app_store: 'Mac App Store',
  play_store: 'Play Store',
  stripe: 'Stripe',
  amazon: 'Amazon',
  promotional: 'Promotional',
  rc_billing: 'RevenueCat Billing',
};

function formatCount(value: number): string {
  return value.toLocaleString();
}

function formatPercent(value: number): string {
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
    maximumFractionDigits: 1,
  })}%`;
}

function storeLabel(store: string | null): string {
  if (!store) return 'Unknown store';
  return STORE_LABELS[store] ?? store;
}

function productLabel(identifier: string | null): string {
  return identifier ?? 'No product identifier';
}

function StatTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm'>
      <p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold tabular-nums text-gray-900'>
        {value}
      </p>
      {hint ? (
        <p className='mt-1 text-xs text-gray-500'>
          {accent ? (
            <span
              className='mr-1.5 inline-block h-2 w-2 rounded-full align-middle'
              style={{ backgroundColor: accent }}
              aria-hidden
            />
          ) : null}
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function PlanBar({
  monthly,
  annual,
  other,
  unknown,
}: {
  monthly: AdminPlanCount;
  annual: AdminPlanCount;
  other: AdminPlanCount;
  unknown: AdminPlanCount;
}) {
  const segments = [monthly, annual, other, unknown].filter((plan) => plan.count > 0);
  if (!segments.length) {
    return (
      <div className='h-4 rounded-full bg-gray-100' aria-hidden />
    );
  }

  return (
    <div
      className='flex h-4 overflow-hidden rounded-full bg-gray-100'
      role='img'
      aria-label={`Monthly ${formatPercent(monthly.percent)}, annual ${formatPercent(annual.percent)}`}
    >
      {segments.map((segment) => (
        <div
          key={segment.plan}
          className='h-full min-w-[2px]'
          style={{
            width: `${Math.max(segment.percent, 0)}%`,
            backgroundColor: PLAN_COLORS[segment.plan],
          }}
          title={`${PLAN_LABELS[segment.plan]}: ${formatCount(segment.count)} (${formatPercent(segment.percent)})`}
        />
      ))}
    </div>
  );
}

function AdminSubscribersPage() {
  const { getToken } = useAuth();

  useEffect(() => {
    track('Admin Subscribers Screen');
  }, []);

  const breakdownQuery = useQuery({
    queryKey: ['admin', 'subscription-breakdown'],
    queryFn: () =>
      apiJson<AdminSubscriptionBreakdownResponse>(
        getToken,
        '/v1/admin/metrics/subscription-breakdown',
      ),
  });

  const data = breakdownQuery.data;

  return (
    <div className='space-y-6'>
      <div className='min-w-0'>
        <h1 className='text-xl font-semibold text-gray-900 sm:text-2xl'>
          Subscribers
        </h1>
        <p className='mt-1 text-sm text-gray-500'>
          Current monthly vs annual split among paying Harbor subscribers. Plan is
          inferred from the cached RevenueCat product identifier.
        </p>
      </div>

      {breakdownQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent' />
        </div>
      ) : breakdownQuery.isError || !data ? (
        <p className='text-red-600'>Could not load subscriber breakdown.</p>
      ) : (
        <>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5'>
            <StatTile
              label='Paying subscribers'
              value={formatCount(data.paying_subscribers)}
              hint={
                data.override_only
                  ? `+ ${formatCount(data.override_only)} admin override${data.override_only === 1 ? '' : 's'}`
                  : 'Excludes admin overrides'
              }
            />
            <StatTile
              label='Monthly'
              value={formatCount(data.monthly.count)}
              hint={formatPercent(data.monthly.percent)}
              accent={PLAN_COLORS.monthly}
            />
            <StatTile
              label='Annual'
              value={formatCount(data.annual.count)}
              hint={formatPercent(data.annual.percent)}
              accent={PLAN_COLORS.annual}
            />
            <StatTile
              label='Other'
              value={formatCount(data.other.count)}
              hint={
                data.other.count
                  ? `${formatPercent(data.other.percent)} · lifetime or unmatched`
                  : 'Lifetime or unmatched'
              }
              accent={PLAN_COLORS.other}
            />
            <StatTile
              label='Unknown plan'
              value={formatCount(data.unknown.count)}
              hint={
                data.unknown.count
                  ? `${formatPercent(data.unknown.percent)} · no product id`
                  : 'No cached product id'
              }
              accent={PLAN_COLORS.unknown}
            />
          </div>

          <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
            <div className='mb-4'>
              <h2 className='text-lg font-semibold text-gray-900'>Plan mix</h2>
              <p className='mt-1 text-sm text-gray-500'>
                Share of paying subscribers on each plan.
              </p>
            </div>
            <PlanBar
              monthly={data.monthly}
              annual={data.annual}
              other={data.other}
              unknown={data.unknown}
            />
            <div className='mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600'>
              {(['monthly', 'annual', 'other', 'unknown'] as const).map((plan) => (
                <span key={plan} className='inline-flex items-center gap-1.5'>
                  <span
                    className='h-2 w-2 rounded-full'
                    style={{ backgroundColor: PLAN_COLORS[plan] }}
                    aria-hidden
                  />
                  {PLAN_LABELS[plan]} {formatPercent(data[plan].percent)}
                </span>
              ))}
            </div>
          </section>

          <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
            <div className='mb-4'>
              <h2 className='text-lg font-semibold text-gray-900'>
                Product identifiers
              </h2>
              <p className='mt-1 text-sm text-gray-500'>
                Raw RevenueCat product IDs behind the monthly / annual grouping.
              </p>
            </div>
            {data.products.length ? (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead>
                    <tr className='border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>
                      <th className='pb-2 pr-4 font-semibold'>Product</th>
                      <th className='pb-2 pr-4 font-semibold'>Plan</th>
                      <th className='pb-2 pr-4 text-right font-semibold'>Count</th>
                      <th className='pb-2 text-right font-semibold'>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.products.map((row) => (
                      <tr
                        key={`${row.product_identifier ?? 'none'}-${row.plan}`}
                        className='border-b border-gray-100 last:border-0'
                      >
                        <td className='py-2 pr-4 font-mono text-xs text-gray-800'>
                          {productLabel(row.product_identifier)}
                        </td>
                        <td className='py-2 pr-4'>
                          <span className='inline-flex items-center gap-1.5'>
                            <span
                              className='h-2 w-2 rounded-full'
                              style={{ backgroundColor: PLAN_COLORS[row.plan] }}
                              aria-hidden
                            />
                            {PLAN_LABELS[row.plan]}
                          </span>
                        </td>
                        <td className='py-2 pr-4 text-right tabular-nums'>
                          {formatCount(row.count)}
                        </td>
                        <td className='py-2 text-right tabular-nums text-gray-600'>
                          {formatPercent(row.percent)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className='text-sm text-gray-500'>No paying subscribers yet.</p>
            )}
          </section>

          <section className='rounded-lg border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
            <div className='mb-4'>
              <h2 className='text-lg font-semibold text-gray-900'>By store</h2>
              <p className='mt-1 text-sm text-gray-500'>
                Same paying-subscriber snapshot, split by RevenueCat store.
              </p>
            </div>
            {data.stores.length ? (
              <div className='overflow-x-auto'>
                <table className='min-w-full text-sm'>
                  <thead>
                    <tr className='border-b border-gray-200 text-left text-xs font-semibold uppercase tracking-wide text-gray-500'>
                      <th className='pb-2 pr-4 font-semibold'>Store</th>
                      <th className='pb-2 pr-4 text-right font-semibold'>Monthly</th>
                      <th className='pb-2 pr-4 text-right font-semibold'>Annual</th>
                      <th className='pb-2 pr-4 text-right font-semibold'>Other</th>
                      <th className='pb-2 pr-4 text-right font-semibold'>Unknown</th>
                      <th className='pb-2 text-right font-semibold'>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.stores.map((row) => (
                      <tr
                        key={row.store ?? 'none'}
                        className='border-b border-gray-100 last:border-0'
                      >
                        <td className='py-2 pr-4'>{storeLabel(row.store)}</td>
                        <td className='py-2 pr-4 text-right tabular-nums'>
                          {formatCount(row.monthly)}
                        </td>
                        <td className='py-2 pr-4 text-right tabular-nums'>
                          {formatCount(row.annual)}
                        </td>
                        <td className='py-2 pr-4 text-right tabular-nums'>
                          {formatCount(row.other)}
                        </td>
                        <td className='py-2 pr-4 text-right tabular-nums'>
                          {formatCount(row.unknown)}
                        </td>
                        <td className='py-2 text-right tabular-nums font-medium'>
                          {formatCount(row.total)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className='text-sm text-gray-500'>No store data yet.</p>
            )}
          </section>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/subscribers')({
  component: AdminSubscribersPage,
});
